import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LanguageCode, LANGUAGES_LIST } from '@/data/languages';
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface LanguageSelectorProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  languageCode: LanguageCode;
  setLanguageCode: (languageCode: LanguageCode) => void;
  disabled?: boolean;
}

function LanguageSelector({ open, setOpen, languageCode, setLanguageCode, disabled }: LanguageSelectorProps) {
  const { t } = useTranslation('storefront');
  const currentLanguage = LANGUAGES_LIST.find((language) => language.code === languageCode);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
          disabled={disabled}
        >
          {languageCode ? (
            <div className="flex items-center gap-2">
              <img
                src={`https://flagcdn.com/w320/${currentLanguage?.flagCode}.png`}
                width="20"
                alt={currentLanguage?.flagCode}
              />{' '}
              {currentLanguage?.name} - {currentLanguage?.code}
            </div>
          ) : (
            t('configEditor.formElements.selectLanguage')
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder={t('configEditor.formElements.searchLanguage')} />
          <CommandEmpty>{t('configEditor.formElements.noLanguageFound')}</CommandEmpty>
          <CommandGroup className="max-h-[300px] overflow-auto">
            {LANGUAGES_LIST.map((language) => (
              <CommandItem
                key={language.code}
                value={language.code}
                onSelect={(value) => {
                  setLanguageCode(value as LanguageCode);
                }}
              >
                <Check
                  className={cn('mr-2 h-4 w-4', languageCode === language.code ? 'opacity-100' : 'opacity-0')}
                />
                <img
                  src={`https://flagcdn.com/w320/${language.flagCode}.png`}
                  width="20"
                  alt={language.flagCode}
                  className="mr-2"
                />{' '}
                {language.name} - {language.code}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default LanguageSelector;
